// Function to handle single content block removal (for the trash can button)

import {
  extractS3Key,
  UnresolvableMediaKeyError,
} from '@trilirivos/media-keys';
import { TextBlock } from '../interfaces/textEditor.interface';
import { deleteImageFromS3 } from './deleteImagefromS3';

/**
 * What a block's image resolves to. THE SINGLE DERIVATION (TRIL-615 review).
 *
 * This fact used to be derived twice — once here to decide whether to delete, and once in
 * removeBlockWithFeedback to decide what to TELL the user — and the bug was those two disagreeing.
 * For an Image block at an unrecognized URL, `extractS3Key` returns null, so the second derivation
 * concluded "there was no image" and showed "Something went wrong removing that block. Please try
 * again." on a block that was already gone, for an image that may still be public. Exactly the
 * inverse of what the copy is documented to do.
 *
 * `hasImage` is about the BLOCK; `key` is about whether we could act on it. They are different
 * questions and the copy needs both.
 */
export interface BlockImage {
  /** True when the block is an Image block carrying a url — regardless of whether it parsed. */
  hasImage: boolean;
  /** The url as stored on the block, so a failure can name what it could not resolve. */
  url: string | null;
  /** The owner-bound S3 key, or null when it could not be resolved. */
  key: string | null;
}

export const resolveBlockImage = (block: TextBlock): BlockImage => {
  const url = block.type === 'Image' ? block.data?.file?.url : undefined;
  if (!url) return { hasImage: false, url: null, key: null };
  return { hasImage: true, url, key: extractS3Key(url) };
};

/**
 * Remove one content block, and delete its image from S3 when it has one.
 *
 * ONE COPY, DELIBERATELY. This existed as two BYTE-IDENTICAL files —
 * PostDraft/DraftFormRender/handleRemoveContentBlock.ts and the PostReplyDraft twin — and only the
 * first had a __tests__ directory. So deleting the delete call from the reply copy left the entire
 * client suite green: the reply composer could silently lose this fix and nothing would say so.
 *
 * That is the failure this whole arc kept repeating (fix one of a pair) turned structural. A second
 * test file would have covered the symptom; one module makes the divergence unrepresentable.
 *
 * ORDER IS DELIBERATE. Removing the block from the UI is what the user asked for and must always
 * happen; the S3 delete is the part that can fail. Deleting first and letting a throw abort the
 * removal would leave the user unable to remove a block at all whenever S3 is unhappy.
 *
 * The delete failure is RE-THROWN rather than logged, because the caller needs to tell the user
 * their image was not actually withdrawn. Swallowing it is the original bug: the block vanished
 * from the screen while the object stayed in a publicly readable bucket.
 */
export const handleRemoveContentBlock = async (
  block: TextBlock,
  userId: string,
  draftId: string,
  // biome-ignore lint/suspicious/noExplicitAny: Formik arrayHelpers
  arrayHelpers: any,
  index: number,
  /** Pass the caller's already-resolved value so the fact is derived exactly once. */
  resolved: BlockImage = resolveBlockImage(block),
): Promise<void> => {
  arrayHelpers.remove(index);

  // An Image block that HAS a url we cannot turn into a key is the bug under repair wearing a
  // different hat: the block disappears, nothing is deleted, and nobody is told. A block with no
  // url at all is the ordinary not-yet-uploaded state and is not an error.
  if (resolved.hasImage && !resolved.key) {
    throw new UnresolvableMediaKeyError(resolved.url ?? '');
  }

  if (resolved.key) {
    await deleteImageFromS3(resolved.key);
  }
};
